import { Backdrop, Button, CircularProgress, useTheme, Box, Typography } from '@mui/material'
import { InventoryInspection, Issue } from '../../types/inspection'
import { axiosInstance } from '../../helpers'
import { useAppDispatch } from '../../hooks'
import { setInventoryInspection } from '../../store/Inspection/action'
import { FlexDiv } from '../../components'
import { AddAPhoto, Delete } from '@mui/icons-material'
import { useState } from 'react'
import imageCompression from 'browser-image-compression'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

interface Props {
    index: number
    issue: Issue
    inventoryInspection: InventoryInspection
}

export const IssueImage = (props: Props) => {
    const { issue, index, inventoryInspection } = props

    const [loading, setLoading] = useState(false)
    const [fullScreen, setFullScreen] = useState(false)

    const displayName = issue.issue_config
        ? issue.issue_config.name
        : issue.description

    const theme = useTheme()
    const dispatch = useAppDispatch()

    const areaInspection = useSelector(
        (state: RootState) => state.inspection.areaInspection,
    )

    return issue.message.attachments.length > index ? (
        <Box sx={{ position: 'relative' }}>
            <Box
                component="img"
                src={issue.message.attachments[index].file}
                alt={`user uploaded image for ${displayName}`}
                sx={{
                    height: 100,
                    width: 100,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: `2px solid ${theme.palette.grey[600]}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                        transform: 'scale(1.02)',
                    }
                }}
                onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    setFullScreen(true)
                }}
            />

            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    flexDirection: 'column',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                }}
                open={fullScreen}
                onClick={() => {
                    setFullScreen(false)
                }}
            >
                <Box
                    component="img"
                    src={issue.message.attachments[index].file}
                    alt={`user uploaded image for ${displayName}`}
                    sx={{
                        maxHeight: '80%',
                        maxWidth: '90%',
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<Delete />}
                    onClick={() => {
                        axiosInstance
                            .post(
                                'inspection/tenant-inspection/remove_image_from_issue/',
                                {
                                    issue_id: issue.id,
                                    image_id:
                                        issue.message.attachments[index].id,
                                },
                            )
                            .then((res) => {
                                const issues = inventoryInspection.issue.map(
                                    (innerIssue) => {
                                        if (innerIssue.id !== res.data.id) {
                                            return innerIssue
                                        }
                                        return res.data
                                    },
                                )
                                dispatch(
                                    setInventoryInspection({
                                        ...inventoryInspection,
                                        issue: issues,
                                    }),
                                )
                                setFullScreen(false)
                            })
                    }}
                    sx={{
                        backgroundColor: theme.palette.error.main,
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        marginTop: 2,
                        padding: theme.spacing(1, 3),
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: theme.palette.error.dark,
                        }
                    }}
                >
                    Delete Image
                </Button>
            </Backdrop>
        </Box>
    ) : (
        <Box>
            {issue.message.attachments.length === index &&
                areaInspection?.status === 'READY' && (
                    <label htmlFor={`icon-button-file_${issue.id}`}>
                        <Box
                            sx={{
                                border: `2px dashed ${theme.palette.grey[600]}`,
                                width: 100,
                                height: 100,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: 'rgba(0, 140, 133, 0.1)',
                                }
                            }}
                        >
                            {loading ? (
                                <CircularProgress
                                    size={24}
                                    sx={{ color: theme.palette.primary.main }}
                                />
                            ) : (
                                <FlexDiv direction="column" style={{ alignItems: 'center' }}>
                                    <AddAPhoto
                                        style={{
                                            color: theme.palette.primary.main,
                                            fontSize: 24,
                                            marginBottom: 4
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        style={{
                                            color: theme.palette.primary.main,
                                            fontSize: 10,
                                            textAlign: 'center',
                                        }}
                                    >
                                        Add Photo
                                    </Typography>
                                </FlexDiv>
                            )}
                        </Box>
                    </label>
                )}
            <input
                accept="image/png, image/jpeg, image/jpg"
                id={`icon-button-file_${issue.id}`}
                type="file"
                style={{ display: 'none' }}
                onChange={async (e) => {
                    e.stopPropagation()
                    e.preventDefault()

                    if (e.target.files && e.target.files[0]) {
                        setLoading(true)
                        const compressedImage = await imageCompression(
                            e.target.files[0],
                            { maxSizeMB: 0.5 },
                        )
                        const reqBody = new FormData()
                        reqBody.append('issue_id', String(issue.id))
                        reqBody.append('attachments_len', '1')
                        reqBody.append('attachment0', compressedImage)
                        axiosInstance
                            .post(
                                'inspection/tenant-inspection/add_images_to_issue/',
                                reqBody,
                            )
                            .then((res) => {
                                const issues = inventoryInspection.issue.map(
                                    (innerIssue) => {
                                        if (issue.id !== innerIssue.id) {
                                            return innerIssue
                                        }
                                        return {
                                            ...innerIssue,
                                            message: res.data,
                                        }
                                    },
                                )
                                dispatch(
                                    setInventoryInspection({
                                        ...inventoryInspection,
                                        issue: issues,
                                    }),
                                )
                            })
                            .finally(() => {
                                setLoading(false)
                            })
                    }
                }}
            />
        </Box>
    )
}
